import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Bell, Users, ArrowLeftRight, Inbox } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { listNotifications, type NotificationItem } from "@/lib/notifications.functions";

// Bell + dropdown in the dashboard header. Unread = items newer than the
// locally stored last-seen timestamp; opening the panel marks everything
// seen (new items keep their highlight until the panel closes).

const LAST_SEEN_KEY = "rtn:notifications:lastSeen";

const KIND_META = {
  lead: { icon: Users, to: "/leads" },
  match: { icon: ArrowLeftRight, to: "/exchange" },
  request: { icon: Inbox, to: "/requests" },
} as const;

function timeAgo(iso: string): string {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function readLastSeen(): string {
  if (typeof window === "undefined") return new Date(0).toISOString();
  try {
    return localStorage.getItem(LAST_SEEN_KEY) ?? new Date(0).toISOString();
  } catch {
    return new Date(0).toISOString();
  }
}

export function NotificationCenter() {
  const { t } = useTranslation();
  const fetchNotifications = useServerFn(listNotifications);

  // Wait for the Supabase session to hydrate before firing the authed
  // serverFn — otherwise the bearer attacher sends no token and 401s.
  const [hasSession, setHasSession] = useState(false);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setHasSession(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setHasSession(!!session);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => fetchNotifications({ data: {} }),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
    enabled: hasSession,
  });
  const items: NotificationItem[] = data?.items ?? [];

  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(readLastSeen);
  // Snapshot of lastSeen at open time so freshly seen items stay
  // highlighted while the panel is open.
  const [highlightSince, setHighlightSince] = useState(lastSeen);

  const unread = items.filter((i) => i.ts > lastSeen).length;

  function toggle() {
    setOpen((v) => {
      const next = !v;
      if (next) {
        setHighlightSince(lastSeen);
        const now = new Date().toISOString();
        setLastSeen(now);
        try {
          localStorage.setItem(LAST_SEEN_KEY, now);
        } catch {
          // Private mode etc. — badge simply stays.
        }
      }
      return next;
    });
  }

  return (
    <div className="relative">
      <button
        onClick={toggle}
        aria-label={t("app.shell.notifications.title")}
        className="relative flex items-center justify-center rounded-full border border-border bg-card p-2 text-muted-foreground hover:text-foreground transition"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-medium text-background">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-[min(92vw,360px)] rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-[13px] font-medium">
              {t("app.shell.notifications.title")}
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-8 text-center text-[13px] text-muted-foreground">
                  {t("app.shell.notifications.empty")}
                </div>
              ) : (
                items.map((item) => {
                  const meta = KIND_META[item.kind];
                  const fresh = item.ts > highlightSince;
                  return (
                    <Link
                      key={item.id}
                      to={meta.to}
                      onClick={() => setOpen(false)}
                      className="flex items-start gap-3 px-4 py-3 border-b border-border/60 last:border-0 hover:bg-background/60 transition"
                    >
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground">
                        <meta.icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 text-[13px] text-foreground">
                          <span className="truncate font-medium">
                            {t(`app.shell.notifications.${item.kind}`)}
                            {item.name ? ` · ${item.name}` : ""}
                          </span>
                          {fresh && (
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                          )}
                        </span>
                        {item.detail && (
                          <span className="block truncate text-[12px] text-muted-foreground">
                            {item.detail}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {timeAgo(item.ts)}
                      </span>
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
